/**
 * Parent-endpoint directions derived from placement anchors.
 *
 * `ANCHOR_PATTERNS_BY_LABEL` answers "is this node placed?" with one-hop
 * patterns. Plant session paint needs the inverse question per edge type —
 * "which endpoint is the parent container?" — so marks can roll up and ghosts
 * can sit under a parent. Pattern direction maps as:
 *
 *   `in`  → anchored node is the target → parent at relationship start
 *   `out` → anchored node is the source → parent at relationship end
 *
 * Directions are declared once per relationshipSpec (`anchors: source|target`),
 * so a single edge type cannot legitimately conflict across labels. The conflict
 * list is exported so tests lock that invariant.
 */
import { ANCHOR_PATTERNS_BY_LABEL } from '../generated/types/anchor-patterns'
import type { RelationshipType } from '../generated/types/relationships'

export type ParentEndpoint = 'start' | 'end'

export interface AnchorParentEndpointConflict {
  type: RelationshipType
  a: ParentEndpoint
  b: ParentEndpoint
}

function endpointForDirection(direction: 'in' | 'out'): ParentEndpoint {
  return direction === 'in' ? 'start' : 'end'
}

function deriveAnchorParentEndpoints(): {
  byType: Readonly<Partial<Record<RelationshipType, ParentEndpoint>>>
  conflicts: readonly AnchorParentEndpointConflict[]
} {
  const byType = new Map<RelationshipType, ParentEndpoint>()
  const conflicts: AnchorParentEndpointConflict[] = []
  for (const patterns of Object.values(ANCHOR_PATTERNS_BY_LABEL)) {
    for (const pattern of patterns) {
      const endpoint = endpointForDirection(pattern.direction)
      const existing = byType.get(pattern.type)
      if (existing !== undefined && existing !== endpoint) {
        conflicts.push({ type: pattern.type, a: existing, b: endpoint })
        continue
      }
      byType.set(pattern.type, endpoint)
    }
  }
  return {
    byType: Object.fromEntries(byType) as Partial<Record<RelationshipType, ParentEndpoint>>,
    conflicts
  }
}

const derived = deriveAnchorParentEndpoints()

/** Parent endpoint per relationship type, folded from placement anchors. */
export const ANCHOR_PARENT_ENDPOINT_BY_TYPE: Readonly<
  Partial<Record<RelationshipType, ParentEndpoint>>
> = derived.byType

/**
 * Empty under a healthy schema. Non-empty means two labels anchored the same
 * edge type on opposite endpoints — regenerate / fix relationships.json.
 */
export const ANCHOR_PARENT_ENDPOINT_CONFLICTS: readonly AnchorParentEndpointConflict[] =
  derived.conflicts
